import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { placementAckAPI, publicSettingsAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SignatureCanvas from 'react-signature-canvas';
import { CheckCircle, Loader2, Eraser, Building2, Calendar, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const PlacementAcknowledgment = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [logo, setLogo] = useState(null);
  const sigRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [ackRes, logoRes] = await Promise.all([
          placementAckAPI.get(token),
          publicSettingsAPI.get().catch(() => null),
        ]);
        setData(ackRes.data);
        if (logoRes?.data?.logo) setLogo(logoRes.data.logo);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast.error('Please provide your signature');
      return;
    }
    setSubmitting(true);
    try {
      const signature = sigRef.current.toDataURL('image/png');
      await placementAckAPI.sign(token, { signature });
      setData((prev) => ({ ...prev, ack_status: 'signed', ack_signature: signature }));
      toast.success('Acknowledgment signed successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-ica-navy" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <p className="text-gray-500">Invalid or expired acknowledgment link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isSigned = data.ack_status === 'signed';

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center">
          {logo && <img src={logo} alt="ICA Logo" className="h-16 mx-auto mb-4" />}
          <h1 className="font-heading text-2xl font-bold text-ica-navy">Placement Acknowledgment</h1>
          <p className="text-gray-500 mt-1">Please review your placement details and sign below</p>
        </div>

        {/* Placement Details */}
        <Card>
          <CardContent className="pt-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-ica-navy/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-ica-navy font-semibold text-sm">{data.student_name?.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Student</p>
                  <p className="font-medium text-gray-900">{data.student_name}</p>
                  <p className="text-sm text-gray-500">{data.student_number}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-ica-gold/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-ica-gold" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Restaurant</p>
                  <p className="font-medium text-gray-900">{data.restaurant_name}</p>
                  {data.restaurant_address && <p className="text-sm text-gray-500">{data.restaurant_address}</p>}
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Position</p>
                  <p className="font-medium text-gray-900">{data.position}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Period</p>
                  <p className="font-medium text-gray-900">{data.start_date} to {data.end_date}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Signature */}
        <Card>
          <CardContent className="pt-6">
            {isSigned ? (
              <div className="text-center space-y-4">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto" />
                <h3 className="text-lg font-semibold text-gray-900">Acknowledgment Signed</h3>
                <p className="text-sm text-gray-500">
                  Signed on {data.ack_signed_at?.split('T')[0] || ''}
                </p>
                {data.ack_signature && (
                  <div className="border rounded-lg p-4 bg-gray-50 inline-block">
                    <img src={data.ack_signature} alt="Signature" className="max-h-24" />
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    By signing below, I acknowledge that I have read and understood the placement details above.
                  </p>
                </div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white">
                  <SignatureCanvas
                    ref={sigRef}
                    canvasProps={{
                      className: 'w-full',
                      style: { width: '100%', height: '150px' },
                    }}
                    penColor="#1e3a5f"
                  />
                </div>
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => sigRef.current?.clear()}
                    type="button"
                  >
                    <Eraser className="w-4 h-4 mr-1" /> Clear
                  </Button>
                  <Button
                    data-testid="sign-acknowledgment-btn"
                    onClick={handleSign}
                    disabled={submitting}
                    
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Sign Acknowledgment
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-gray-400">Mizanplaz - The ICA Placement App</p>
      </div>
    </div>
  );
};

export default PlacementAcknowledgment;
